import React, {useEffect} from 'react';
import {Container, Stack, ToggleButton, ToggleButtonGroup, Box} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {connect} from "react-redux";
import {usersActions} from "../../modules/users/users.actions";
import Loader from "./layout/loading/loader";
import ButtonCreate from "./layout/ButtonCreate";
import Constants from "../../utils/constants";
import ClientSelector from "./patrimoine/ClientSelector";
import PatrimoineTable from "./patrimoine/PatrimoineTable";
import {isAdmin, isClient, isExploitant} from "../../utils/roles";
import PageTitle from "./layout/PageTitle";
import {FolderDelete} from "@mui/icons-material";
import {helper} from "../../utils/helper";
import {campaignsActions} from "../../modules/campaigns/campaigns.actions";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import CSVHandler from "../../utils/CSVHandler.js";
import ConteneurSearchBar from "./patrimoine/ConteneurSearchBar";
import ClientSelectorV2 from "./patrimoine/ClientSelectorV2.js";
import ConteneurMapView from "./patrimoineV2/ConteneurMapView";

const PATRIMOINE_VIEW_MODE_KEY = 'patrimoineViewMode';

function PatrimoinePage({dispatch, ...props}) {
    const [viewMode, setViewMode] = React.useState(
        window.localStorage.getItem(PATRIMOINE_VIEW_MODE_KEY) || 'classique'
    );
    const [admin, setAdmin] = React.useState(isAdmin())
    const [client] = React.useState(isClient())
    const currentUser = helper.getCurrentUser();
    const [clientSelected, setClientSelected] = React.useState(client ? String(currentUser.id) : null);
    const [exploitantSelected, setExploitantSelected] = React.useState(window.localStorage.getItem('exploitantSelected'));
    const [exploitants, setExploitants] = React.useState([]);

    useEffect(() => {
        if (client) {
            // Si l'utilisateur est un client, on auto-sélectionne son propre compte
            // et on n'a pas besoin de récupérer la liste des clients.
            return;
        }
        if (!props.clients && !props.loading && !props.error) {
            dispatch(usersActions.fetchClients())
        }

        if (props.clients && Array.isArray(props.clients)) {
            const uniqueExploitants = {};
            const extractedExploitants = [];

            // Filter clients to show only those with "lavage" service type
            const lavageClients = props.clients.filter(client => 
                client.serviceTypes && client.serviceTypes.includes("lavage")
            );

            for (const client of lavageClients) {
                const exploitant = client.exploitant;
                if (exploitant && !uniqueExploitants[exploitant.id]) {
                    uniqueExploitants[exploitant.id] = true;
                    extractedExploitants.push(exploitant);
                }
            }

            setExploitants(extractedExploitants);

            const selectedClient = window.localStorage.getItem('clientSelected')

            // Mettre à jour clientSelected si nécessaire
            const clientIds = lavageClients.map(client => client.id);
            if (!clientIds.includes(Number(selectedClient))) {
                setClientSelected(null);
                window.localStorage.removeItem('clientSelected');
            } else {
                setClientSelected(selectedClient)
            }
        }
    }, [dispatch, props.clients, props.error, props.loading]);

    useEffect(() => {
        if (clientSelected) {
            const storeClientId = props.client ? String(props.client.id) : null;
            if (!storeClientId || storeClientId !== String(clientSelected)) {
                dispatch(usersActions.fetchUser("client", clientSelected));
            }
        }

    }, [clientSelected, dispatch, props.client]);

    useEffect(() => {
        return () => {
            // dispatch(usersActions.clearUser());
        };
    }, []);

    const filteredClients = React.useMemo(() => {
        let data = [];
        if (admin) {
            if (exploitantSelected && Array.isArray(props.clients)) {
                for (const client of props.clients) {
                    // Only include clients with "lavage" service type
                    if (client.exploitant.id == exploitantSelected && 
                        client.serviceTypes && client.serviceTypes.includes("lavage")) {
                        data.push(client);
                    }
                }
            }
        } else {
            // Filter clients to only show those with "lavage" service type
            data = props.clients ? props.clients.filter(client => 
                client.serviceTypes && client.serviceTypes.includes("lavage")
            ) : [];
        }
        data?.sort((a, b) => a.libelle.localeCompare(b.libelle));
        return data;
    }, [admin, exploitantSelected, props.clients]);


    const onClientSelected = (clientId) => {
        window.localStorage.setItem('clientSelected', clientId);
        setClientSelected(clientId)
        dispatch(usersActions.fetchUser("client", clientId));
    }
    const onExploitantSelected = (exploitantId) => {
        window.localStorage.setItem('exploitantSelected', exploitantId);
        setExploitantSelected(exploitantId)
    }

    const onViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            window.localStorage.setItem(PATRIMOINE_VIEW_MODE_KEY, newMode);
            setViewMode(newMode);
        }
    }

    const handleDelete = (p) => {
        dispatch(patrimoinesActions.deletePatrimoine(p.id, () => {
            dispatch(usersActions.fetchUser("client", clientSelected));
        }));
    }

    const sortedPatrimoine = (data) => {
        // Sort patrimoines by name (no filtering needed since we filter at client level)
        data.sort((a, b) => a.libelle.localeCompare(b.libelle));
        return data;
    }

    const handleExportCSV = (patrimoine) => {
        dispatch(patrimoinesActions.fetchPatrimoine(patrimoine.id, (patrimoineWithConteneur) => {
            CSVHandler.exportPatrimoineToCSV(patrimoineWithConteneur)
        }))
    }

    return (
        <>
            <Stack direction={"row"} alignItems={"center"} spacing={2} >
                <PageTitle title={"Gestion des patrimoines"} icon={<FolderDelete/>}/>
                {(isAdmin() || isExploitant()) &&
                  <Box sx={{flexShrink: 0, whiteSpace: 'nowrap'}}>
                    <ButtonCreate route={Constants.APP_ROUTE_PATRIMOINE_NEW.replace(":id", clientSelected)}
                                  label="Ajouter un patrimoine"/>
                  </Box>
                }
            </Stack>
	        <Stack direction={"row"} alignItems={"end"} sx={{mt:0, mb:0}}>
	        <Box sx={{ml: 'auto'}}>
		        <ToggleButtonGroup
		          value={viewMode}
		          exclusive
		          size="small"
		          color="primary"
		          onChange={onViewModeChange}
		        >
			        <ToggleButton value="classique">Affichage classique</ToggleButton>
			        <ToggleButton value="beta">Affichage beta</ToggleButton>
		        </ToggleButtonGroup>
	        </Box>
	        </Stack>
            {!client &&
                <ClientSelectorV2 clients={filteredClients} isAdmin={admin} onClientSelected={onClientSelected}
                                clientSelected={clientSelected} exploitants={exploitants}
                                exploitantSelected={exploitantSelected}
                                onExploitantSelected={onExploitantSelected}/>
            }

            {props.loading &&
                <Loader/>
            }

            {viewMode === 'classique' ? (
                <>
                    {clientSelected &&
                        <Grid container justifyContent={"center"} className={""} sx={{mt: 2}}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <ConteneurSearchBar clientSelected={clientSelected}/>
                            </Grid>
                        </Grid>
                    }

                    {(clientSelected && props.client && String(props.client.id) === String(clientSelected)) &&
                        <Grid container size={12}>

                            <Grid size={12} style={{position: 'relative'}} className={""}>
                                <PatrimoineTable patrimoines={sortedPatrimoine(props.client.patrimoines)}
                                                 handleExportCSV={handleExportCSV}
                                                 onDelete={handleDelete}/>
                            </Grid>

                        </Grid>
                    }
                </>
            ) : (
                clientSelected &&
                    <ConteneurMapView key={clientSelected} clientId={clientSelected}/>
            )}

            {/*<ButtonCreate route={Constants.APP_ROUTE_ADMIN_USERS_NEW} label="Ajouter un utilisateur"/>*/}
            {/*<UsersTable users={props.users} onDelete={handleDeleteUser}/>*/}
        </>
    )
}

const mapStateToProps = function (state) {
    return {
        clients: state.users.clients,
        client: state.users.user,
        loading: state.users.loading || state.patrimoines.loading,
        error: state.users.error || state.patrimoines.error
    }
}

export default connect(mapStateToProps)(PatrimoinePage);
